import { useState } from "react";
import { FileText, Check, Circle, CircleDot } from "lucide-react";
import { toast } from "sonner";
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  Button,
  Pill,
} from "@/design/components";
import { useOrgIndex } from "@/domain/hooks";
import { useKycActions } from "@/domain/useMutations";
import type { KycReview } from "@/domain/types";
import { cn } from "@/design/cn";

export function KycDetailDrawer({
  review,
  open,
  onOpenChange,
}: {
  review: KycReview | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const orgs = useOrgIndex();
  const { setAccepted, decide: submitKycDecision, isPending } = useKycActions();
  const [mode, setMode] = useState<null | "query" | "reject">(null);
  const [comment, setComment] = useState("");

  if (!review) return null;
  const org = orgs[review.organisationId];
  const requiredAllAccepted = review.requiredDocuments.filter((d) => d.required).every((d) => d.accepted);

  async function decide(decision: KycReview["state"]) {
    if ((decision === "queried" || decision === "rejected") && !comment.trim()) return;
    await submitKycDecision(review!.id, decision, comment.trim() || undefined);
    const verb = decision === "approved" ? "approved" : decision === "queried" ? "queried" : "rejected";
    toast[decision === "approved" ? "success" : "message"](`KYC ${verb} for ${org?.name}`);
    setMode(null);
    setComment("");
    onOpenChange(false);
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} width="960px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">{org?.name}</DrawerTitle>
        <div className="mt-1 flex items-center gap-2">
          <Pill variant={review.tier === "prime" ? "brand" : "neutral"}>{review.tier === "prime" ? "Prime" : "Subcontractor"}</Pill>
          <Pill variant={review.state === "approved" ? "success" : review.state === "queried" ? "warn" : review.state === "rejected" ? "danger" : "info"}>
            {review.state.replace("_", " ")}
          </Pill>
        </div>
      </DrawerHeader>

      <DrawerBody className="p-0">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          {/* Left: documents */}
          <div className="space-y-4 border-line p-5 lg:col-span-7 lg:border-r">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-faint">Uploaded documents</h4>
            <ul className="space-y-2">
              {review.requiredDocuments.map((d) => (
                <li key={d.key} className="rounded-md border border-line p-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-faint" />
                    <span className="flex-1 text-sm font-medium text-ink">{d.label}</span>
                    {!d.required && <Pill variant="outline">Optional</Pill>}
                    <Pill variant={d.documentId ? "info" : "neutral"}>{d.documentId ? "Uploaded" : "Missing"}</Pill>
                  </div>
                  {d.documentId && (
                    <a
                      href="/seed-evidence/document.svg"
                      target="_blank"
                      rel="noreferrer"
                      className="focus-ring mt-2 flex items-center gap-3 rounded-md border border-line bg-card p-2 hover:bg-subtle"
                    >
                      <img src="/seed-evidence/document.svg" alt="" className="h-16 w-12 shrink-0 rounded border border-line object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium text-ink">{d.label}.pdf</p>
                        <p className="text-[11px] text-faint">PDF · open to review</p>
                      </div>
                      <FileText className="h-4 w-4 shrink-0 text-faint" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
            {review.structuredFields && review.structuredFields.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Structured fields</h4>
                <dl className="grid grid-cols-2 gap-2">
                  {review.structuredFields.map((f) => (
                    <div key={f.label} className="rounded-md bg-subtle px-3 py-2">
                      <dt className="text-xs text-faint">{f.label}</dt>
                      <dd className="text-sm text-ink">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>

          {/* Right: review controls */}
          <div className="space-y-4 p-5 lg:col-span-5">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-faint">Tier requirements</h4>
            <ul className="space-y-1.5">
              {review.requiredDocuments.filter((d) => d.required).map((d) => (
                <li key={d.key}>
                  <button
                    onClick={() => setAccepted(review.id, d.key, !d.accepted)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                      d.accepted ? "border-ok/40 bg-okSoft text-ok" : d.documentId ? "border-line hover:bg-subtle" : "border-line text-faint",
                    )}
                  >
                    {d.accepted ? <Check className="h-4 w-4" /> : d.documentId ? <CircleDot className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                    <span className="flex-1 text-ink">{d.label}</span>
                    <span className="text-xs">{d.accepted ? "Accepted" : d.documentId ? "Present" : "Missing"}</span>
                  </button>
                </li>
              ))}
            </ul>

            {review.reviewerComments.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Comments</h4>
                <ul className="space-y-2">
                  {review.reviewerComments.map((c) => (
                    <li key={c.id} className="rounded-md bg-subtle px-3 py-2 text-sm text-mute">
                      {c.body}
                      <span className="mt-1 block text-[11px] text-faint">{c.visibility === "internal" ? "Internal Stratus" : "Shared with contractor"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </DrawerBody>

      <DrawerFooter className="flex-col items-stretch gap-2">
        {mode ? (
          <div className="space-y-2">
            <textarea
              autoFocus
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={mode === "reject" ? "Reason for rejection (required)" : "Comment for the contractor (required)"}
              className="h-20 w-full rounded-md border border-line p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setMode(null); setComment(""); }}>Cancel</Button>
              <Button variant={mode === "reject" ? "danger" : "primary"} onClick={() => decide(mode === "reject" ? "rejected" : "queried")} disabled={isPending || !comment.trim()}>
                {mode === "reject" ? "Reject submission" : "Send query"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-xs text-faint">{requiredAllAccepted ? "All required items accepted" : "Accept all required items to approve"}</span>
            <div className="flex gap-2">
              <Button variant="ghost" disabled={isPending} onClick={() => setMode("reject")}>Reject</Button>
              <Button variant="secondary" disabled={isPending} onClick={() => setMode("query")}>Query</Button>
              <Button disabled={isPending || !requiredAllAccepted} onClick={() => decide("approved")}>Approve</Button>
            </div>
          </div>
        )}
      </DrawerFooter>
    </Drawer>
  );
}
